import { Button, Col, Divider, Form, Row } from 'antd';
import { RegistStepTwoPayload } from 'domain/entities/UserEntity';
import { openErrorNoti, openSuccessNoti } from 'presentation/components/Notification';
import { FormInput } from 'presentation/components/form/FormInput.component';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { MEMBER_TYPE_PERSONAL } from '../../CU-003-01';
import { StepProps } from '../props/StepProps';
import UploadImage from './Upload';

export const RegistrationStepTwo = ({ prev, setIsChange, ...props }: StepProps) => {
    const [btnDisable, setBtnDisable] = useState<boolean>(true);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [requiredLoanAmountHouse, setRequiredLoanAmountHouse] = useState<boolean>(false);
    const [requiredLoanAmountCar, setRequiredLoanAmountCar] = useState<boolean>(false);
    const [requiredLoanAmountProperty, setRequiredLoanAmountProperty] = useState<boolean>(false);
    const [requiredLoanAmountOther, setRequiredLoanAmountOther] = useState<boolean>(false);
    const [requiredFileTaxSlip, setRequiredFileTaxSlip] = useState<boolean>(false);
    const [requiredFileFinalReport, setRequiredFileFinalReport] = useState<boolean>(true);

    useEffect(() => {
        props.scrollTop && props.scrollTop();
    }, []);

    const onChangeForm = () => {
        form.validateFields({
            validateOnly: true,
        })
            .then((formValues) => {
                if (formValues.fileTaxSlip) {
                    setRequiredFileFinalReport(false);
                    setRequiredFileTaxSlip(true);
                } else if (formValues.fileFinalReport) {
                    setRequiredFileTaxSlip(false);
                    setRequiredFileFinalReport(true);
                }
                setBtnDisable(validateFiles(formValues));
            })
            .catch((err) => {
                if (err.values.fileTaxSlip) {
                    setRequiredFileFinalReport(false);
                    setRequiredFileTaxSlip(true);
                } else if (err.values.fileFinalReport) {
                    setRequiredFileTaxSlip(false);
                    setRequiredFileFinalReport(true);
                }
                setBtnDisable(err.errorFields.length > 0 ? true : validateFiles(err.values));
            });
    };

    const validateFiles = (formValues: any) => {
        // fileFinalReport, fileTaxSlip はフラグ付きでそのまま評価
        const isFinalReportInvalid = !formValues?.fileFinalReport && requiredFileFinalReport;
        const isTaxSlipInvalid = !formValues?.fileTaxSlip && requiredFileTaxSlip;
        // ローン関連（1円以上でファイル必須）※判定時はカンマを除去
        const loanConditions = [
            {
                amount: Number(commonHelpers.replace(String(formValues?.loanAmountCar ?? ''))),
                file: formValues?.fileLoanAmountCar,
            },
            {
                amount: Number(commonHelpers.replace(String(formValues?.loanAmountHouse ?? ''))),
                file: formValues?.fileLoanAmountHouse,
            },
            {
                amount: Number(commonHelpers.replace(String(formValues?.loanAmountOther ?? ''))),
                file: formValues?.fileLoanAmountOther,
            },
            {
                amount: Number(commonHelpers.replace(String(formValues?.loanAmountProperty ?? ''))),
                file: formValues?.fileLoanAmountProperty,
            },
        ];

        let isAnyLoanFileMissing = false;
        for (let i = 0; i < loanConditions.length; i++) {
            if (loanConditions[i].amount > 0 && loanConditions[i].file === undefined) {
                isAnyLoanFileMissing = true;
                break;
            }
        }
        return isFinalReportInvalid || isTaxSlipInvalid || isAnyLoanFileMissing;
    };

    const submitNext = async () => {
        try {
            dispatch(startFetching());
            const formValues = form.getFieldsValue();
            const payload: RegistStepTwoPayload = {
                income: Number(commonHelpers.replace(formValues.income)),
                loanAmountCar: Number(commonHelpers.replace(formValues.loanAmountCar)),
                loanAmountHouse: Number(commonHelpers.replace(formValues.loanAmountHouse)),
                loanAmountOther: Number(commonHelpers.replace(formValues.loanAmountOther)),
                loanAmountProperty: Number(commonHelpers.replace(formValues.loanAmountProperty)),
                memberType: MEMBER_TYPE_PERSONAL,
                fileFinalReport: getFileSubmit('fileFinalReport'),
                fileLoanAmountCar: getFileSubmit('fileLoanAmountCar'),
                fileLoanAmountHouse: getFileSubmit('fileLoanAmountHouse'),
                fileLoanAmountOther: getFileSubmit('fileLoanAmountOther'),
                fileLoanAmountProperty: getFileSubmit('fileLoanAmountProperty'),
                fileTaxSlip: getFileSubmit('fileTaxSlip'),
                isEditFileTax: form.getFieldValue('isEditFileTax') ?? '[]',
                isEditFileReport: form.getFieldValue('isEditFileReport') ?? '[]',
                isEditFileCar: form.getFieldValue('isEditFileCar') ?? '[]',
                isEditFileHouse: form.getFieldValue('isEditFileHouse') ?? '[]',
                isEditFileOther: form.getFieldValue('isEditFileOther') ?? '[]',
                isEditFileProperty: form.getFieldValue('isEditFileProperty') ?? '[]',
            };
            const response = await props.userUseCase.regsitUserStep2(payload);
            if (response) {
                openSuccessNoti({ description: getMessage('S0315') });
            }
            navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_003_01}`);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const getFileSubmit = (name: string) => {
        return form.getFieldValue(name)?.file?.originFileObj;
    };

    useEffect(() => {
        props.scrollTop && props.scrollTop();
    }, []);

    return (
        <>
            <Form
                form={form}
                onValuesChange={() => {
                    onChangeForm();
                    setIsChange && setIsChange(true);
                }}
                onFinish={submitNext}
            >
                <Row className="min-w-min">
                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">収入証明</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={24} className="pr-[12px]">
                                <span className="content-step-form label-step-form">
                                    給与所得者は以下のいずれか
                                </span>
                                <span className="content-step-form label-step-form dotted-text">
                                    源泉徴収票
                                </span>
                                <span className="content-step-form label-step-form dotted-text">
                                    確定申告書及び付属明細（確定申告している場合）
                                </span>
                                <span className="content-step-form label-step-form">
                                    ※いずれも直近のもの
                                </span>
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={24} className="pr-[12px]">
                                <span className="content-step-form label-step-form">
                                    個人事業主は以下
                                </span>
                                <span className="content-step-form label-step-form dotted-text">
                                    確定申告書及び付属明細（直近1期分）
                                </span>
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={24} className="pr-[12px]">
                                <span className="content-step-form label-step-form">
                                    会社経営者は以下のいずれか
                                </span>
                                <span className="content-step-form label-step-form dotted-text">
                                    源泉徴収票
                                </span>
                                <span className="content-step-form label-step-form dotted-text">
                                    確定申告書及び付属明細（確定申告している場合）
                                </span>
                                <span className="content-step-form label-step-form">
                                    ※いずれも直近1期分
                                </span>
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="所得額"
                                    name="income"
                                    type="number"
                                    required
                                    maxLength={10}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    suffix="万円"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px] ant-col-upload">
                                <UploadImage
                                    required={requiredFileTaxSlip}
                                    form={form}
                                    name="fileTaxSlip"
                                    title="収入証明（源泉徴収票）をアップロード"
                                    onChange={onChangeForm}
                                    editName="isEditFileTax"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px] ant-col-upload">
                                <UploadImage
                                    required={requiredFileFinalReport}
                                    form={form}
                                    name="fileFinalReport"
                                    title="収入証明（確定申告書及び付属証明書）をアップロード"
                                    onChange={onChangeForm}
                                    editName="isEditFileReport"
                                />
                            </Col>
                        </Row>
                    </div>

                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">借入証明</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={24} className="pr-[12px]">
                                <span className="content-step-form label-step-form">
                                    ローン返済表
                                </span>
                                <span className="content-step-form label-step-form">
                                    <p className="mt-[0] mr-[5px] mb-[0] ml-[5px]">1.</p>自宅
                                </span>
                                <span className="content-step-form label-step-form">
                                    <p className="mt-[0] mr-[5px] mb-[0] ml-[5px]">2.</p>自動車
                                </span>
                                <span className="content-step-form label-step-form">
                                    <p className="mt-[0] mr-[5px] mb-[0] ml-[5px]">3.</p>
                                    投資用不動産
                                </span>
                                <span className="content-step-form label-step-form">
                                    ※いずれも直近のもの
                                </span>
                                <span className="content-step-form label-step-form">
                                    ※借入額が存在する場合、金額を記載のうえ該当する書類のアップロードが必要です
                                </span>
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="借入額（自宅）"
                                    name="loanAmountHouse"
                                    type="number"
                                    required
                                    maxLength={10}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    suffix="万円"
                                    onChange={(e) => {
                                        setRequiredLoanAmountHouse(e > 0);
                                    }}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="借入額（自動車）"
                                    name="loanAmountCar"
                                    type="number"
                                    required
                                    maxLength={10}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    suffix="万円"
                                    onChange={(e) => {
                                        setRequiredLoanAmountCar(e > 0);
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px] mb-[24px] ant-col-upload">
                                <UploadImage
                                    required={requiredLoanAmountHouse}
                                    form={form}
                                    name="fileLoanAmountHouse"
                                    title="借入証明（自宅）をアップロード"
                                    onChange={onChangeForm}
                                    editName="isEditFileHouse"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px] mb-[24px] ant-col-upload">
                                <UploadImage
                                    required={requiredLoanAmountCar}
                                    form={form}
                                    name="fileLoanAmountCar"
                                    title="借入証明（自動車）をアップロード"
                                    onChange={onChangeForm}
                                    editName="isEditFileCar"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="借入額（投資用不動産）"
                                    name="loanAmountProperty"
                                    type="number"
                                    required
                                    maxLength={10}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    suffix="万円"
                                    onChange={(e) => {
                                        setRequiredLoanAmountProperty(e > 0);
                                    }}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="借入額（その他）"
                                    name="loanAmountOther"
                                    type="number"
                                    required
                                    maxLength={10}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    suffix="万円"
                                    onChange={(e) => {
                                        setRequiredLoanAmountOther(e > 0);
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px] ant-col-upload">
                                <UploadImage
                                    required={requiredLoanAmountProperty}
                                    form={form}
                                    name="fileLoanAmountProperty"
                                    title="借入証明（投資用不動産）をアップロード"
                                    onChange={onChangeForm}
                                    editName="isEditFileProperty"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px] ant-col-upload">
                                <UploadImage
                                    required={requiredLoanAmountOther}
                                    form={form}
                                    name="fileLoanAmountOther"
                                    title="借入証明（その他）をアップロード"
                                    onChange={onChangeForm}
                                    editName="isEditFileOther"
                                />
                            </Col>
                        </Row>
                    </div>

                    <Row justify="center" className="w-full mb-6">
                        <Col span={12} style={{ paddingRight: '8px' }}>
                            <Button
                                size="large"
                                className="button-container previous"
                                onClick={() => prev && prev()}
                            >
                                戻る
                            </Button>
                        </Col>
                        <Col span={12} style={{ paddingLeft: '8px' }}>
                            <Button
                                type="primary"
                                size="large"
                                disabled={btnDisable}
                                onClick={() => submitNext()}
                                className="button-container"
                            >
                                登録する
                            </Button>
                        </Col>
                    </Row>
                </Row>
            </Form>
        </>
    );
};
